/** CUPIS: a book belongs to an exact market AND outcome, never to an event. */
import { polyEventSlug } from '../poly/coupon';
import { indexByName, parseJsonList, type GammaMarket, type LegTokens } from './polyBook';

export interface CupisPolyLeg {
  url: string | null;
  asset: string | null;
  marketKey?: string | null;
  side?: string | null;
  want?: string | null;
}

/** A server-verified asset or an explicitly pinned market is safe to cache. */
export function cupisBookKey(leg: { url?: string | null; asset?: string | null }): string | null {
  if (leg.asset) return `a:${leg.asset}`;
  if (!leg.url) return null;
  try {
    const url = new URL(leg.url);
    const slug = url.searchParams.get('marketSlug');
    const index = url.searchParams.get('outcomeIndex');
    return slug && (index === '0' || index === '1') ? `m:${slug}:${index}` : null;
  } catch { return null; }
}

/** No prefix, first-market, fuzzy-question or default-Yes fallbacks. */
export function exactCupisTokens(markets: readonly GammaMarket[], leg: CupisPolyLeg): LegTokens | null {
  const event = leg.url ? polyEventSlug(leg.url) : null;
  if (!event) return null;
  const url = new URL(leg.url!);
  const pinned = url.searchParams.get('marketSlug');
  const rawIndex = url.searchParams.get('outcomeIndex');
  const key = (leg.marketKey ?? '').split(':');
  const candidates: LegTokens[] = [];
  for (const market of markets) {
    if (market.closed || market.active === false || market.acceptingOrders === false || market.enableOrderBook === false) continue;
    if (market.slug !== event && !market.slug?.startsWith(`${event}-`)) continue;
    const ids = market.version === 'v2' ? parseJsonList(market.positionIds) : parseJsonList(market.clobTokenIds);
    const outcomes = parseJsonList(market.outcomes);
    if (ids.length !== 2 || outcomes.length !== 2) continue;
    let index: 0 | 1 | null = null;
    if (pinned) {
      if (market.slug !== pinned || (rawIndex !== '0' && rawIndex !== '1')) continue;
      index = rawIndex === '1' ? 1 : 0;
    } else if (key[0] === 'WIN' && key[2] === 'M' && market.slug === event) {
      // Binary match winner; H/A alone is not a Gamma outcome index.
      index = leg.want ? indexByName(outcomes, leg.want) : null;
    } else if (/Both to score|BTS-[YN]|обе забьют/i.test(leg.want ?? '') || ['BTS', 'BTTS'].includes(key[0] ?? '')) {
      if (market.sportsMarketType !== 'both_teams_to_score') continue;
      const side = leg.side || (leg.want?.includes('BTS-N') ? 'N' : leg.want?.includes('BTS-Y') ? 'Y' : '');
      const wanted = side === 'N' ? 'no' : side === 'Y' ? 'yes' : '';
      const at = outcomes.findIndex(one => one.toLowerCase() === wanted);
      if (at === 0 || at === 1) index = at;
    }
    if (index !== null) candidates.push({ mine: ids[index]!, other: ids[1 - index]! });
  }
  return candidates.length === 1 ? candidates[0]! : null;
}
